<template>
  <div>
    <TableNavigation
      :summary-route="{
        name: isTenderPage($route)
          ? 'tender-info'
          : isIncomingTenderPage($route)
          ? 'incoming-tender-summary'
          : 'summary',
      }"
      :summary-class="{
        current: isCurrent(
          isTenderPage($route)
            ? 'tender-info'
            : isIncomingTenderInfoPage($route)
            ? 'incoming-tender-info'
            : 'summary'
        ),
      }"
      :background-color="backgroundColor"
      :scrollbar-class="{ 'flex-1': isIncomingTenderPage($route) }"
      :show-scroll-actions="showScrollActions && needShowScroll"
    >
      <template slot="before-tabs">
        <Tab
          v-if="showOffersTab"
          :title="isTenderPage($route) ? $t('Summary') : $t('Offers')"
          :selected="isCurrent(isTenderPage($route) ? 'tender-offers' : 'incoming-tender-offers')"
          colored
          @click.native="goToSummaryOffers"
        />
      </template>
      <template>
        <Tab
          v-for="(list, i) in lists"
          :key="`${list.id}${listInfoRefresher}`"
          colored
          :index="i"
          :selected="isCurrent('list', list.id)"
          @click.native="goToList(list)"
        >
          <template #title>
            <span
              :id="`list-title-${list.id}`"
              class="no-wrap"
              :contenteditable="renameId === list.id"
              @blur="(e) => rename(list.id, e.target.innerText, e.target)"
              @keydown.enter.prevent="(e) => e.target.blur()"
              @dblclick.stop="(e) => goToRename(e.target, list)"
            >
              {{ list.title || 'Untitled' }}
            </span>
          </template>
          <DropdownMenu v-if="!(isTenderPage($route) || isIncomingTenderPage($route))">
            <DropdownMenuElement @click.native="(e) => goToRename(e.target, list)">
              {{ $t('Rename list') }}
              <template slot="icon">
                <Icon type="pencil" size="16" />
              </template>
            </DropdownMenuElement>
            <DropdownMenuElement @click.native="deleteList(list.id)">
              {{ $t('Delete list') }}
              <template slot="icon">
                <Icon type="trash" size="16" />
              </template>
            </DropdownMenuElement>
          </DropdownMenu>
        </Tab>
      </template>
      <template slot="after-tabs">
        <div v-if="showScrollActions && needShowScroll" class="scroll-btn-wrapper spacex-8">
          <Button size="small" @click="scrollLeft">
            <ChevronIcon class="turn-right-90" />
          </Button>
          <Button size="small" @click="scrollRight">
            <ChevronIcon class="turn-left-90" />
          </Button>
        </div>
        <div v-if="!isTenderTypePage" class="after-tabs">
          <button
            type="button"
            class="button-linear button-small w-inline-block add-list flex-centering spacex-8"
            @click="newList"
          >
            <PlusIcon class="icon-14px mr4" />
            <span class="text text-white fs-14 fw-6">
              <!--TODO: добавить импорт-->
              {{ $t('Add list') }}
            </span>
          </button>
        </div>
      </template>
    </TableNavigation>
  </div>
</template>

<script>
  import { mapState, mapActions, mapGetters } from 'vuex';
  import factory from '@/utils/api/factory';
  import PlusIcon from '@/assets/icons/rounded-plus.svg';
  import { DEFAULT_COLOR } from '@/utils/constants/common';

  import {
    isTenderSummaryPage,
    isIncomingTenderPage,
    isTenderPage,
    isIncomingTenderInfoPage,
  } from '@/utils/route-matcher';
  import ChevronIcon from '@/assets/icons/chevron-down.svg';
  import Button from '@/components/ui/Button';
  import TableNavigation from '@/components/incoming-tender/TableNavigation';
  import Tab from '@/components/tabs/Tab';
  import DropdownMenu from '@/components/ui/dropdown-menu/DropdownMenu';
  import DropdownMenuElement from '@/components/ui/dropdown-menu/DropdownMenuElement';
  import Icon from '@/components/ui/Icon';

  export default {
    name: 'ProjectNavigation',
    components: {
      DropdownMenuElement,
      DropdownMenu,
      Tab,
      TableNavigation,
      Button,
      PlusIcon,
      ChevronIcon,
      Icon,
    },
    props: {
      project: Object,
      isTenderTypePage: Boolean,
      showScrollActions: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        renameId: null,
        DEFAULT_COLOR,
        isIncludeTaxes: true,
        scrollBar: null,
        needShowScroll: false,
        listInfoRefresher: false,
        titleBeforeEditing: '',
      };
    },
    computed: {
      ...mapState({
        isLoading: (state) => state.global.isTableLoading,
        projectUpd: (state) => state.global.projectUpdate,
        isSaved: (state) => state.list.isSaved,
        incomingTenderInfo: (state) => state.incomingTender.incomingTenderInfo,
        tenderOffers: (state) => state.tender.tenderOffers,
        lists() {
          if (this.isIncomingTenderPage(this.$route)) {
            return this.incomingTenderInfo?.lists || [];
          } else if (this.isTenderPage(this.$route)) {
            return this.tenderOffers?.lists || [];
          }
          return this.project?.lists || [];
        },
        ...mapGetters({
          getUnreads: 'auth/getUnreads',
        }),
        notifications() {
          const count = this.project?.comments_count?.aggregate.count;
          const last = this.project?.last_comment?.[0]?.date;
          if (!count || !last) return { unreads: 0 };

          return {
            unreads: this.getUnreads(this.project?.id, 'all-proj', { date: last }, count),
            count: count,
            last_date: last,
          };
        },
        backgroundColor() {
          return this.project?.color || DEFAULT_COLOR;
        },
      }),
      showOffersTab() {
        return (
          this.isTenderPage(this.$route) ||
          (this.isIncomingTenderPage(this.$route) &&
            this.incomingTenderInfo.status !== 'invited' &&
            this.incomingTenderInfo.status !== 'declined')
        );
      },
    },
    methods: {
      ...mapActions({
        openContext: 'context/open',
        removeAction: 'context/removeAction',
        projectUpdate: 'global/changeProjectUpdate',
        confirm: 'confirmModal/openModal',
        showAlert: 'alert/showAlert',
      }),
      isIncomingTenderPage,
      isIncomingTenderInfoPage,
      isTenderPage,
      isTenderSummaryPage,
      goToSummaryOffers() {
        this.$router.push({
          name: isTenderPage(this.$route) ? 'tender-summary' : 'incoming-tender-offers',
        });
      },
      goToList(list) {
        this.$router.push({
          name: isTenderPage(this.$route)
            ? 'tender-list'
            : isIncomingTenderPage(this.$route)
            ? 'incoming-tender-list'
            : 'list',
          params: isTenderPage(this.$route)
            ? { tenderListId: list.id }
            : isIncomingTenderPage(this.$route)
            ? { incomingTenderListId: list.id }
            : { list: list.id },
        });
        this.load();
      },
      scrollRight() {
        let scrollBar = document.querySelector('#table-navigation-scroll');
        scrollBar.scrollLeft += scrollBar.offsetWidth * 0.25;
      },
      scrollLeft() {
        let scrollBar = document.querySelector('#table-navigation-scroll');
        scrollBar.scrollLeft -= scrollBar.offsetWidth * 0.25;
      },
      isCurrent(name, id) {
        if (name === 'list') {
          return this.isList(id);
        }
        if (name === 'tender' || name === 'incoming-tender') {
          return this.isTenderSummaryPage(this.$route);
        }
        return this.$route.name === name;
      },
      isList(id) {
        return (
          id === parseInt(this.$route.params.tenderListId) ||
          id === parseInt(this.$route.params.list) ||
          id === parseInt(this.$route.params.incomingTenderListId)
        );
      },
      goToRename(el, list) {
        if (!this.isTenderPage(this.$route) && !this.isIncomingTenderPage(this.$route)) {
          this.titleBeforeEditing = list.title;
          this.renameId = list.id;
          this.$nextTick(() => {
            const span =
              el?.closest('.tab-btn')?.querySelector('span') ||
              document.querySelector(`#list-title-${list.id}`);
            if (span) {
              let range = document.createRange();
              range.selectNodeContents(span);
              let selection = window.getSelection();
              selection.removeAllRanges();
              selection.addRange(range);
            }
          });
        }
      },
      async rename(id, name, el) {
        if (name.trim() !== this.titleBeforeEditing) {
          if (name.trim()) {
            try {
              await factory('lists').updateInPath(id, {
                title: name.trim() || 'Untitled',
              });
              await this.projectUpdate(true);
            } catch (e) {
              this.listInfoRefresher = !this.listInfoRefresher;
              this.showAlert({
                type: 'error',
                text: e.response?.data?.title?.[0] || '',
              });
            }
          } else {
            el.innerText = 'Untitled';
          }
        }
        this.titleBeforeEditing = '';
        this.renameId = null;
      },
      async newList() {
        try {
          const { data } = await factory('lists').create({
            project_id: this.$route.params.id,
          });
          // После создания листа создаем ему строчки по умолчанию
          await this.newRowInList({ order: 0, level: 1, id: data.id });
          await this.newRowInList({ order: 1, level: 0, id: data.id });
        } catch (e) {
          console.error(e);
        }
        await this.projectUpdate(true);
      },
      async newRowInList({ order, level = 0, id }) {
        await factory('rows').create({
          list_id: id,
          order: order || order === 0 ? order + 1 : undefined,
          group_level: level,
        });
      },
      async deleteList(id) {
        this.confirm({
          title: 'Delete list',
          description: `Are you sure you want to delete this list of the project?`,
          actionText: 'Delete',
          action: async () => {
            if (parseInt(this.$route.params.list) === id) this.$router.push({ name: 'summary' });

            try {
              await factory('lists').delete(id);
            } catch (e) {
              this.showAlert({
                type: 'error',
                text:
                  e.response?.data?.non_field_errors?.[0] ||
                  this.$t('You can not delete list with rows participating in any tender'),
              });
            }
            this.projectUpdate(true);
          },
        });
      },
      load() {
        this.$store.dispatch('global/toggleTableLoading', true);
        const el = document.querySelector('.table.table-scroll');
        if (el) el.scrollTo(0, 0);
      },
      switchTaxes() {
        this.isIncludeTaxes = !this.isIncludeTaxes;
      },
    },
    watch: {
      lists() {
        setTimeout(() => {
          let scrollBar = document.querySelector('#table-navigation-scroll');
          this.needShowScroll = scrollBar.scrollWidth > scrollBar.offsetWidth;
        }, 0);
      },
    },
  };
</script>
